import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: 'About', path: '/', icon: 'User' },
    { name: 'Skills', path: '/skills', icon: 'Zap' },
    { name: 'Certifications', path: '/certifications', icon: 'Award' },
    { name: 'Projects', path: '/projects', icon: 'FolderOpen' },
    { name: 'Experience', path: '/experience', icon: 'Briefcase' },
    { name: 'Contact', path: '/contact', icon: 'Mail' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-security ${
        isScrolled 
          ? 'bg-surface/95 backdrop-blur-md security-shadow-strong' 
          : 'bg-surface/80 backdrop-blur-sm'
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center space-x-3 group"
              onClick={closeMobileMenu}
            >
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center group-hover:scale-105 transition-security">
                  <span className="text-white font-bold text-lg">M</span>
                </div>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-foreground group-hover:text-primary transition-security">
                  Musa Yusuf Magoban
                </h1>
                <p className="text-xs text-text-secondary font-medium">
                  SOC Analyst
                </p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-security ${
                  isActivePath(item.path)
                    ? 'bg-primary/10 text-primary border border-primary/20' :'text-foreground hover:bg-muted hover:text-primary'
                }`}
              >
                <Icon 
                  name={item.icon} 
                  size={16} 
                  color={isActivePath(item.path) ? 'var(--color-primary)' : 'currentColor'} 
                />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Desktop CTA - Single focused action */}
          <div className="hidden lg:flex items-center">
            <Button
              variant="default"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="cyber-gradient"
              onClick={() => window.location.href = '/contact'}
            >
              Hire Me
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="text-foreground hover:bg-muted"
            >
              <Icon 
                name={isMobileMenuOpen ? "X" : "Menu"} 
                size={24} 
                color="currentColor" 
              />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-surface border-t border-border security-shadow">
            <div className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-security ${
                    isActivePath(item.path)
                      ? 'bg-primary/10 text-primary border border-primary/20' :'text-foreground hover:bg-muted hover:text-primary'
                  }`}
                >
                  <Icon 
                    name={item.icon} 
                    size={20} 
                    color={isActivePath(item.path) ? 'var(--color-primary)' : 'currentColor'} 
                  />
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Security Status Indicator */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-security-green to-transparent opacity-60"></div>
    </header>
  );
};

export default Header;